import React, { useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  Control,
  useFieldArray,
} from "react-hook-form";
import {
  FileText,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { CURRENCIES } from "../../constants/formOptions";
import { useTranslation } from "react-i18next";

interface AwbSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  control: Control<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

/**
 * Air Waybill (AWB) document block — AIR only. Holds the manually-entered
 * fields that populate the AWB (see awb_field_mapping spec). Auto-populated
 * fields (shipper, consignee, airports, airline, flight, MAWB, cargo) come from
 * the rest of the shipment form and are intentionally not repeated here.
 * Rendered as a collapsible accordion, mirroring PricingSection.
 */
export const AwbSection: React.FC<AwbSectionProps> = ({
  register,
  control,
  errors,
  readonly,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const [isCollapsed, setIsCollapsed] = useState(true);

  const awbErrors = errors.airDetails?.awb as any;
  const notifyErrors = awbErrors?.notifyParty;

  const {
    fields: shcFields,
    append: appendShc,
    remove: removeShc,
  } = useFieldArray({
    control,
    name: "airDetails.awb.specialHandlingCodes" as never,
  });

  const {
    fields: remarkFields,
    append: appendRemark,
    remove: removeRemark,
  } = useFieldArray({
    control,
    name: "airDetails.awb.remarks" as never,
  });

  const paymentOptions = [
    { value: "", label: t("placeholders.select") },
    { value: "Prepaid", label: t("awb.paymentPrepaid") },
    { value: "Collect", label: t("awb.paymentCollect") },
  ];

  const rowBtn =
    "h-8 w-8 shrink-0 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#C97A3C] hover:text-[#C97A3C] disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <SectionCard
      icon={FileText}
      title={t("awb.title")}
      collapsible
      onToggle={() => setIsCollapsed((p) => !p)}
      actions={
        isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )
      }
    >
      {isCollapsed ? null : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
            {/* Left column */}
            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                label={t("awb.awbNumber")}
                error={getErrorMessage(awbErrors?.awbNumber)}
              >
                <Input
                  {...register("airDetails.awb.awbNumber")}
                  error={!!awbErrors?.awbNumber}
                  placeholder={t("awb.awbNumberPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.awbDate")}
                error={getErrorMessage(awbErrors?.awbDate)}
              >
                <Input
                  {...register("airDetails.awb.awbDate")}
                  type="date"
                  error={!!awbErrors?.awbDate}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.issuingAgentName")}
                error={getErrorMessage(awbErrors?.issuingAgentName)}
              >
                <Input
                  {...register("airDetails.awb.issuingAgentName")}
                  error={!!awbErrors?.issuingAgentName}
                  placeholder={t("awb.issuingAgentNamePlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.issuingAgentCity")}
                error={getErrorMessage(awbErrors?.issuingAgentCity)}
              >
                <Input
                  {...register("airDetails.awb.issuingAgentCity")}
                  error={!!awbErrors?.issuingAgentCity}
                  placeholder={t("awb.issuingAgentCityPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.currency")}
                error={getErrorMessage(awbErrors?.currency)}
              >
                <Select
                  {...register("airDetails.awb.currency")}
                  options={CURRENCIES}
                  placeholder={t("placeholders.select")}
                  error={!!awbErrors?.currency}
                  disabled={readonly}
                  size="sm"
                />
              </FormField>
            </div>

            {/* Right column */}
            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                label={t("awb.freightPaymentTerms")}
                error={getErrorMessage(awbErrors?.freightPaymentTerms)}
              >
                <Select
                  {...register("airDetails.awb.freightPaymentTerms")}
                  options={paymentOptions}
                  error={!!awbErrors?.freightPaymentTerms}
                  disabled={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.otherChargesPayment")}
                error={getErrorMessage(awbErrors?.otherChargesPayment)}
              >
                <Select
                  {...register("airDetails.awb.otherChargesPayment")}
                  options={paymentOptions}
                  error={!!awbErrors?.otherChargesPayment}
                  disabled={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.declaredValueCarriage")}
                error={getErrorMessage(awbErrors?.declaredValueCarriage)}
              >
                <Input
                  {...register("airDetails.awb.declaredValueCarriage")}
                  error={!!awbErrors?.declaredValueCarriage}
                  placeholder={t("awb.declaredValueCarriagePlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("awb.declaredValueCustoms")}
                error={getErrorMessage(awbErrors?.declaredValueCustoms)}
              >
                <Input
                  {...register("airDetails.awb.declaredValueCustoms")}
                  error={!!awbErrors?.declaredValueCustoms}
                  placeholder={t("awb.declaredValueCustomsPlaceholder")}
                  readOnly={readonly}
                  size="sm"
                />
              </FormField>
            </div>
          </div>

          {/* Special handling codes (IATA SHC) */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">
                {t("awb.specialHandlingCodes")}
              </h4>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => appendShc("" as never)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30]"
                >
                  <Plus size={14} /> {t("awb.addCode")}
                </button>
              )}
            </div>
            {shcFields.length === 0 ? (
              <p className="text-xs text-gray-400">{t("awb.noCodes")}</p>
            ) : (
              <div className="space-y-1.5">
                {shcFields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <Input
                      {...register(
                        `airDetails.awb.specialHandlingCodes.${index}` as any,
                      )}
                      placeholder={t("awb.codePlaceholder")}
                      readOnly={readonly}
                      size="sm"
                    />
                    {!readonly && (
                      <button
                        type="button"
                        onClick={() => removeShc(index)}
                        className={rowBtn}
                        aria-label={t("awb.removeCode")}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Handling information / remarks */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">
                {t("awb.remarks")}
              </h4>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => appendRemark("" as never)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30]"
                >
                  <Plus size={14} /> {t("awb.addRemark")}
                </button>
              )}
            </div>
            {remarkFields.length === 0 ? (
              <p className="text-xs text-gray-400">{t("awb.noRemarks")}</p>
            ) : (
              <div className="space-y-1.5">
                {remarkFields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <Input
                      {...register(`airDetails.awb.remarks.${index}` as any)}
                      placeholder={t("awb.remarkPlaceholder")}
                      readOnly={readonly}
                      size="sm"
                    />
                    {!readonly && (
                      <button
                        type="button"
                        onClick={() => removeRemark(index)}
                        className={rowBtn}
                        aria-label={t("awb.removeRemark")}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notify party */}
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t("awb.notifyParty")}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("awb.notifyCompany")}
                  error={getErrorMessage(notifyErrors?.companyName)}
                >
                  <Input
                    {...register("airDetails.awb.notifyParty.companyName")}
                    error={!!notifyErrors?.companyName}
                    placeholder={t("awb.notifyCompanyPlaceholder")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("awb.notifyContact")}
                  error={getErrorMessage(notifyErrors?.contactPerson)}
                >
                  <Input
                    {...register("airDetails.awb.notifyParty.contactPerson")}
                    error={!!notifyErrors?.contactPerson}
                    placeholder={t("awb.notifyContactPlaceholder")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>
              </div>

              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("labels.email")}
                  error={getErrorMessage(notifyErrors?.email)}
                >
                  <Input
                    {...register("airDetails.awb.notifyParty.email")}
                    type="email"
                    error={!!notifyErrors?.email}
                    placeholder={t("placeholders.email")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("labels.phone")}
                  error={getErrorMessage(notifyErrors?.phone)}
                >
                  <Input
                    {...register("airDetails.awb.notifyParty.phone")}
                    error={!!notifyErrors?.phone}
                    placeholder={t("placeholders.phone")}
                    readOnly={readonly}
                    size="sm"
                  />
                </FormField>
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

AwbSection.displayName = "AwbSection";

export default AwbSection;
